/**
 * ScrollRestoration - Restaure la position de scroll lors des navigations "Retour/Suivant".
 *
 * Clé = location.pathname (robuste face à l'instabilité de location.key en BrowserRouter).
 * - Navigation PUSH (nouvelle page) → remonte en haut.
 * - Navigation POP (Retour/Suivant) → restaure la position sauvegardée pour ce chemin.
 * - Navigation REPLACE (synchro de filtres sur la même page) → on ne touche pas au scroll.
 *
 * IMPORTANT : la sauvegarde se fait via useLayoutEffect pour détacher le listener AVANT
 * le paint de la nouvelle route. Sinon, quand la nouvelle page (plus courte) se rend, le
 * navigateur "clamp" window.scrollY à 0 et déclenche un event scroll qui écraserait la
 * position sauvegardée de la page qu'on quitte.
 *
 * Le contenu défile sur window (cf. DashboardLayout), donc window.scrollY suffit.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'scrollpos:';

// Désactiver la restauration native du navigateur (on la gère nous-mêmes).
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    try { window.history.scrollRestoration = 'manual'; } catch { /* ignore */ }
}

export default function ScrollRestoration() {
    const location = useLocation();
    const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
    const pathRef = useRef(location.pathname);
    // Mémorise le pathname réellement affiché lors de la dernière navigation traitée,
    // pour distinguer un vrai changement de page d'un simple changement de query params
    // (ex. drill-down apporteur/VP, filtres) sur la MÊME page.
    const lastHandledPathRef = useRef(location.pathname);

    // Sauvegarde continue (layout effect : cleanup synchrone avant paint de la route suivante).
    useLayoutEffect(() => {
        pathRef.current = location.pathname;
        const save = () => {
            try { sessionStorage.setItem(PREFIX + pathRef.current, String(window.scrollY)); } catch { /* quota */ }
        };
        window.addEventListener('scroll', save, { passive: true });
        return () => {
            save(); // capture la position courante AVANT le paint de la nouvelle route
            window.removeEventListener('scroll', save);
        };
    }, [location.pathname]);

    // Restauration (POP) ou remontée (PUSH). REPLACE = même page → ne rien faire.
    useEffect(() => {
        const samePage = location.pathname === lastHandledPathRef.current;
        lastHandledPathRef.current = location.pathname;

        if (navType === 'PUSH') {
            // On ne remonte en haut que pour un VRAI changement de page.
            // Un PUSH qui ne change que les query params (même pathname : drill-down,
            // filtres, sélection de scope sur le dashboard…) conserve la position de scroll
            // → pas de saut visuel disgracieux.
            if (!samePage) window.scrollTo(0, 0);
            return;
        }
        if (navType !== 'POP') return;

        let saved = 0;
        try { saved = parseInt(sessionStorage.getItem(PREFIX + location.pathname) || '0', 10) || 0; } catch { /* ignore */ }
        if (saved <= 0) {
            window.scrollTo(0, 0);
            return;
        }

        // Le contenu (listes, fiches) se charge de façon asynchrone : on essaie de
        // restaurer la position tant que la page n'est pas assez haute, jusqu'à un
        // délai max COURT (1.5s) pour éviter de "voler" le contrôle du scroll à
        // l'utilisateur sur mobile.
        // IMPORTANT : dès que l'utilisateur touche/scrolle/tape sur son écran, on
        // ANNULE toute restauration en cours — c'est lui qui reprend la main.
        let cancelled = false;
        const start = Date.now();
        const MAX_MS = 1500;
        const apply = () => {
            const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;
            const target = Math.min(saved, Math.max(0, maxScrollable));
            window.scrollTo(0, target);
            return Math.abs(window.scrollY - target) <= 4;
        };
        const stop = () => {
            cancelled = true;
            if (ro) ro.disconnect();
            window.removeEventListener('wheel', stop, { capture: true });
            window.removeEventListener('touchstart', stop, { capture: true });
            window.removeEventListener('touchmove', stop, { capture: true });
            window.removeEventListener('keydown', stop, { capture: true });
        };
        // Annule la restauration dès la première interaction utilisateur.
        window.addEventListener('wheel', stop, { capture: true, passive: true, once: true });
        window.addEventListener('touchstart', stop, { capture: true, passive: true, once: true });
        window.addEventListener('touchmove', stop, { capture: true, passive: true, once: true });
        window.addEventListener('keydown', stop, { capture: true, once: true });

        const tryRestore = () => {
            if (cancelled) return;
            if (apply()) return;   // arrivé à destination → on stoppe
            if (Date.now() - start < MAX_MS) requestAnimationFrame(tryRestore);
        };
        requestAnimationFrame(tryRestore);

        let ro = null;
        try {
            ro = new ResizeObserver(() => {
                if (cancelled) return;
                // Ne réapplique QUE si l'utilisateur n'a pas encore touché à la page
                // ET tant qu'on est dans la fenêtre de restauration.
                if (Date.now() - start >= MAX_MS) { stop(); return; }
                if (apply()) stop();
            });
            ro.observe(document.documentElement);
        } catch { /* pas de ResizeObserver */ }
        // Sécurité : hard stop à MAX_MS pour libérer le scroll dans tous les cas.
        const hardStopTimer = setTimeout(stop, MAX_MS);
        return () => { clearTimeout(hardStopTimer); stop(); };
    }, [location.pathname, location.key, navType]);

    return null;
}
